import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:opacity-80",
        "contain-primary": "bg-primary text-primary-foreground hover:opacity-80",
        "contain-destructive": "bg-destructive text-destructive-foreground hover:opacity-80",
        "contain-accent": "bg-accent text-accent-foreground hover:opacity-80",
        "contain-secondary": "bg-secondary text-secondary-foreground hover:opacity-80",
        "contain-muted": "bg-muted text-muted-foreground hover:opacity-80",
        "contain-white": "bg-white text-white-foreground hover:opacity-80",
        
        "outline-primary": "border border-input border-primary text-primary bg-background hover:bg-primary hover:text-primary-foreground",
        "outline-seconday": "border border-input border-seconday text-seconday bg-background hover:bg-seconday hover:text-seconday-foreground",
        "outline-accent": "border border-input border-accent text-primary bg-background hover:bg-accent hover:text-accent-foreground",
        "outline-destructive": "border border-input border-destructive text-destructive bg-background hover:bg-destructive hover:text-destructive-foreground",
        "outline-muted": "border border-input border-muted text-muted bg-background hover:bg-muted hover:text-muted-foreground",

        "ghost-primary": "hover:bg-primary hover:text-primary-foreground",
        "ghost-secondary": "hover:bg-secondary hover:text-secondary-foreground",
        "ghost-accent": "hover:bg-accent hover:text-accent-foreground",
        "ghost-destructive": "hover:bg-destructive hover:text-destructive-foreground",

        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean, href?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, href, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <a href={href}>
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
      </a>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
